'use client'

import { useRef, useState } from 'react'
import dynamic from 'next/dynamic'
import { useTheme } from '@mui/material/styles'
import { Box, Container } from '@mui/material'

// Hooks
import useInView from '@/hooks/use-in-view'

// Types
import type { TouchEvent } from 'react'
import type { HomePainsProps } from '@/types/components/organisms/home'

// Components
const RevealAtom = dynamic(() => import('@/components/atoms/animation/reveal'))
const SectionHeadingMolecule = dynamic(() => import('@/components/molecules/solution/section-heading'))
const PainTabMolecule = dynamic(() => import('@/components/molecules/home/pain-tab'))
const PainSlideMolecule = dynamic(() => import('@/components/molecules/home/pain-slide'))

// How long each slide stays before moving to the next one
const STEP_DURATION = 7000
// Minimum finger travel in px that counts as a swipe
const SWIPE_DISTANCE = 50

// Common pains as a horizontal slider driven by a tab bar; it plays on its own, pauses on hover and can be swiped on touch screens
const HomePainsOrganism = (props: HomePainsProps) => {
	// Props
	const { title, subtitle, groups } = props

	// States
	const [active, setActive] = useState(0)
	const [hovered, setHovered] = useState(false)

	// Variables
	const theme = useTheme()
	const rtl = theme.direction === 'rtl'
	const ref = useRef<HTMLDivElement | null>(null)
	const touchStart = useRef<number | null>(null)
	const inView = useInView(ref, 0.4)
	const go = (step: number) => setActive(current => (current + step + groups.length) % groups.length)

	const onTouchStart = (event: TouchEvent) => {
		touchStart.current = event.touches[0].clientX
	}

	const onTouchEnd = (event: TouchEvent) => {
		if (touchStart.current === null) return
		const delta = event.changedTouches[0].clientX - touchStart.current
		touchStart.current = null
		if (Math.abs(delta) < SWIPE_DISTANCE) return
		// In RTL the next slide sits to the left, so dragging right moves forward
		go(delta > 0 === rtl ? 1 : -1)
	}

	return (
		<Container maxWidth="lg" sx={{ py: { xs: 8, md: 14 } }}>
			<RevealAtom>
				<SectionHeadingMolecule title={title} subtitle={subtitle} />
			</RevealAtom>

			<Box ref={ref} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} sx={{ mt: { xs: 5, md: 8 } }}>
				{/* Tab bar; scrolls sideways on small screens */}
				<RevealAtom>
					<Box
						sx={{
							mx: { xs: -2, md: 0 },
							px: { xs: 2, md: 0 },
							display: 'flex',
							gap: { xs: 1, md: 2 },
							overflowX: 'auto',
							scrollbarWidth: 'none',
							'&::-webkit-scrollbar': { display: 'none' }
						}}
					>
						{groups.map((group, index) => (
							<PainTabMolecule
								key={group.label}
								icon={group.icon}
								label={group.label}
								active={index === active}
								running={inView && !hovered}
								duration={STEP_DURATION}
								onSelect={() => setActive(index)}
								onDone={() => go(1)}
							/>
						))}
					</Box>
				</RevealAtom>

				{/* Slides in one row, moved sideways to the active one */}
				<RevealAtom delay={0.1}>
					<Box sx={{ mt: { xs: 3, md: 4 }, overflow: 'hidden', borderRadius: { xs: 5, md: 8 } }} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
						{/* Inline style so the RTL style plugin doesn't flip the offset */}
						<Box
							style={{ transform: `translateX(${(rtl ? 1 : -1) * active * 100}%)` }}
							sx={{
								display: 'flex',
								transition: 'transform .8s cubic-bezier(0.22, 1, 0.36, 1)',
								'@media (prefers-reduced-motion: reduce)': { transition: 'none' }
							}}
						>
							{groups.map((group, index) => (
								<Box key={group.label} aria-hidden={index !== active} sx={{ flex: '0 0 100%' }}>
									<PainSlideMolecule icon={group.icon} title={group.title} items={group.items} image={group.image} active={index === active} />
								</Box>
							))}
						</Box>
					</Box>
				</RevealAtom>
			</Box>
		</Container>
	)
}

export default HomePainsOrganism
